/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import TaxPage from "./TaxPage";
import {
  DECL_TABS,
  DeclarationSnapshot,
  EditTabKey,
  OTHER_SOURCE_LABELS,
  OTHER_SOURCES_INCOME_KEYS,
  OTHER_SOURCES_TDS_KEYS,
  RegimeView,
  TaxLineItem,
  buildRegimes,
  fetchTaxCompare,
  formatAmount,
  formatINR,
  getLoggedInEmpId,
} from "./taxSimplifier.service";

function TaxSection({ title, items }: { title: string; items: TaxLineItem[] }) {
  return (
    <div className="tss-section">
      <div className="tss-section-title">
        <span className="tss-accent" />
        {title}
      </div>
      {items.map((item) => (
        <div className="tss-row" key={item.label}>
          <span>{item.label}</span>
          <strong>{formatINR(item.value)}</strong>
        </div>
      ))}
    </div>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="tss-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function DeclarationSummary({ declaration }: { declaration: DeclarationSnapshot }) {
  const [activeTab, setActiveTab] = useState<EditTabKey>("deductions");
  const yesNo = (v: boolean) => (v ? "Yes" : "No");
  const possessionLabel = (v: "" | "yes" | "no") => (v === "yes" ? "Yes" : v === "no" ? "No" : "-");

  return (
    <div>
      <h5 className="mt-3 mb-2 fw-bold">Declaration Summary</h5>
      <div className="tss-tabs">
        {DECL_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={activeTab === tab.key ? "active" : ""}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`ti ${tab.icon} me-1`} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "deductions" &&
        (declaration.totalIncomeDeductions.length === 0 ? (
          <div className="tss-empty">No data available</div>
        ) : (
          declaration.totalIncomeDeductions.map((row) => (
            <div className="tss-section" key={row.id}>
              <div className="fw-semibold mb-2">{row.label}</div>
              <ReadOnlyRow label="Deduction Limit" value={`₹${formatAmount(row.deductibleAmount)}`} />
              {row.exemptionPercentage !== undefined && (
                <ReadOnlyRow label="Exemption %" value={`${row.exemptionPercentage}%`} />
              )}
              <ReadOnlyRow label="Contribution Amount" value={`₹${row.contributionAmount}`} />
            </div>
          ))
        ))}

      {activeTab === "savingsInvestments" &&
        (declaration.savingsInvestments.length === 0 ? (
          <div className="tss-empty">No data available</div>
        ) : (
          declaration.savingsInvestments.map((row) => (
            <div className="tss-section" key={row.id}>
              <div className="fw-semibold mb-2">{row.label}</div>
              <ReadOnlyRow label="Deduction Limit" value={`₹${formatAmount(row.deductibleAmount)}`} />
              <ReadOnlyRow label="Contribution Amount" value={`₹${row.contributionAmount}`} />
            </div>
          ))
        ))}

      {activeTab === "houseProperty" && (
        <>
          <div className="tss-section">
            <div className="tss-section-title">
              <span className="tss-accent" />
              Loss from Self Occupied House Property
            </div>
            <ReadOnlyRow label="Home Loan Interest Paid" value={`₹${declaration.homeLoanInterestPaid}`} />
            <ReadOnlyRow
              label="Eligible for Housing Loan Interest Deduction"
              value={yesNo(declaration.eligibleForHousingLoanDeduction)}
            />
            <ReadOnlyRow label="Possession Obtained" value={possessionLabel(declaration.selfOccupiedPossession)} />
          </div>
          <div className="fw-semibold mb-2">Let-out Properties</div>
          {declaration.letOutProperties.length === 0 ? (
            <div className="tss-empty">No let-out properties added yet. Tap &quot;Add Property&quot; to add one.</div>
          ) : (
            declaration.letOutProperties.map((property, index) => (
              <div className="tss-section" key={property.id}>
                <div className="fw-semibold mb-2">Property {index + 1}</div>
                <ReadOnlyRow label="Rental Income (INR)" value={`₹${property.rentalIncome}`} />
                <ReadOnlyRow label="Interest Paid (INR)" value={`₹${property.interestPaid}`} />
                <ReadOnlyRow label="TDS on Rent (INR)" value={`₹${property.tdsOnRent}`} />
                <ReadOnlyRow label="Repair Expenditure (INR)" value={`₹${property.repairExpenditure}`} />
                <ReadOnlyRow label="Other Expenses (INR)" value={`₹${property.otherExpenses}`} />
                <ReadOnlyRow
                  label="Eligible for Housing Loan Interest Deduction"
                  value={yesNo(property.eligibleForDeduction)}
                />
                <ReadOnlyRow label="Possession Obtained" value={possessionLabel(property.possessionObtained)} />
              </div>
            ))
          )}
        </>
      )}

      {activeTab === "otherSources" && (
        <>
          <div className="tss-section">
            <div className="tss-section-title">
              <span className="tss-accent" />
              Income from Other Sources
            </div>
            {OTHER_SOURCES_INCOME_KEYS.map((key) => (
              <ReadOnlyRow key={key} label={OTHER_SOURCE_LABELS[key]} value={`₹${declaration.otherSourcesValues[key]}`} />
            ))}
          </div>
          <div className="tss-section">
            <div className="tss-section-title">
              <span className="tss-accent" />
              TDS/TCS on Other Income
            </div>
            {OTHER_SOURCES_TDS_KEYS.map((key) => (
              <ReadOnlyRow key={key} label={OTHER_SOURCE_LABELS[key]} value={`₹${declaration.otherSourcesValues[key]}`} />
            ))}
          </div>
        </>
      )}

      {activeTab === "additional" &&
        (declaration.additionalPayments.length === 0 ? (
          <div className="tss-empty">No additional payments or deductions added yet</div>
        ) : (
          declaration.additionalPayments.map((payment, index) => (
            <div className="tss-section" key={payment.id}>
              <div className="fw-semibold mb-2">Additional Payment {index + 1}</div>
              <ReadOnlyRow label="Additional Payment / Deduction" value={payment.type || "-"} />
              <ReadOnlyRow label="Value" value={`₹${payment.value}`} />
              <ReadOnlyRow label="Unit" value={payment.unit === "PERCENT" ? "%" : "INR"} />
            </div>
          ))
        ))}
    </div>
  );
}

const TaxDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const declaration = (location.state as any)?.declarationSnapshot as DeclarationSnapshot | null;
  const [activeRegime, setActiveRegime] = useState<"old" | "new">("new");
  const [compareData, setCompareData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const empId = getLoggedInEmpId();
      if (!empId) {
        toast.error("Employee ID not found");
        setLoading(false);
        return;
      }
      try {
        const data = await fetchTaxCompare(empId);
        setCompareData(data);
      } catch (err: any) {
        toast.error(err?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const regimes = useMemo(() => buildRegimes(compareData), [compareData]);
  const active: RegimeView = regimes[activeRegime];
  const other = regimes[activeRegime === "old" ? "new" : "old"];

  return (
    <TaxPage crumb="Simulate & Compare">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <button type="button" className="btn btn-link px-0" onClick={() => navigate(all_routes.taxSimplifier)}>
          <i className="ti ti-arrow-left me-1" />
          Back
        </button>
        <Link to={all_routes.taxSimplifierEdit} className="tss-edit-btn">
          <i className="ti ti-pencil" />
          Edit Declarations
        </Link>
      </div>

      <div className="tss-regime">
        {(Object.keys(regimes) as Array<"old" | "new">).map((key) => (
          <button
            key={key}
            type="button"
            className={activeRegime === key ? "active" : ""}
            onClick={() => setActiveRegime(key)}
          >
            {regimes[key].name}
            {regimes[key].selected && <span className="tss-dot" />}
          </button>
        ))}
      </div>

      {active.selected && (
        <div className="tss-banner">This is your currently selected regime for payroll.</div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="text-muted mt-3 mb-0">Loading tax comparison</p>
        </div>
      ) : (
        <>
          <div className="tss-hero">
            <div className="tss-hero-label">Total Annual Tax · {active.name}</div>
            <div className="tss-hero-value">{formatINR(active.totalAnnualTax)}</div>
            <hr className="my-2" />
            <div className="tss-hero-row">
              <span>{other.name}</span>
              <strong>{formatINR(other.totalAnnualTax)}</strong>
            </div>
          </div>
          <TaxSection title="Gross Salary Details" items={active.grossSalary} />
          <TaxSection title="Exemptions u/s 10 & Deductions u/s 16" items={active.exemptions} />
          <TaxSection title="Taxable Income" items={active.taxableIncome} />
          <TaxSection title="Tax Calculation" items={active.taxCalculation} />
          {declaration && <DeclarationSummary declaration={declaration} />}
        </>
      )}
    </TaxPage>
  );
};

export default TaxDetails;
